import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiSave, FiUserPlus, FiTrash2, FiToggleLeft, FiToggleRight } from 'react-icons/fi';

const emptyEmployee = { name: '', email: '', password: '' };

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [empForm, setEmpForm] = useState(emptyEmployee);
  const [savingEmp, setSavingEmp] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get('/settings'),
        api.get('/auth/employees'),
      ]);
      setSettings(s.data.settings);
      setEmployees(e.data.employees);
    } catch (err) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const setField = (field, val) => setSettings(prev => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings', settings);
      setSettings(res.data.settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setField('shopLogo', reader.result);
    reader.readAsDataURL(file);
  };

  const addEmployee = async () => {
    if (!empForm.name || !empForm.email || !empForm.password) {
      toast.error('All fields are required');
      return;
    }
    setSavingEmp(true);
    try {
      await api.post('/auth/employees', { ...empForm, role: 'employee' });
      toast.success('Employee added');
      setShowEmpModal(false);
      setEmpForm(emptyEmployee);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add employee');
    }
    setSavingEmp(false);
  };

  const toggleEmployee = async (id) => {
    try {
      await api.put(`/auth/employees/${id}/toggle`);
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const deleteEmployee = async (id, name) => {
    if (!window.confirm(`Remove employee "${name}"?`)) return;
    try {
      await api.delete(`/auth/employees/${id}`);
      toast.success('Employee removed');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  if (loading || !settings) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div>
      {/* Shop Info */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">🏪 Shop Information</h3></div>
        <div className="card-body">
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Shop Name</label>
              <input className="form-control" value={settings.shopName || ''}
                onChange={e => setField('shopName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Shop Logo</label>
              <input type="file" accept="image/*" className="form-control" onChange={handleLogoUpload} />
              {settings.shopLogo && (
                <img src={settings.shopLogo} alt="logo" style={{ height: 40, marginTop: 8, borderRadius: 4 }} />
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Shop Address</label>
            <textarea className="form-control" rows={2} value={settings.shopAddress || ''}
              onChange={e => setField('shopAddress', e.target.value)} />
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Shop Phone</label>
              <input className="form-control" value={settings.shopPhone || ''}
                onChange={e => setField('shopPhone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Shop Email</label>
              <input type="email" className="form-control" value={settings.shopEmail || ''}
                onChange={e => setField('shopEmail', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Settings */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">🧾 Billing Settings</h3></div>
        <div className="card-body">
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Currency</label>
              <input className="form-control" value={settings.currency || ''}
                onChange={e => setField('currency', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">VAT Percent (%)</label>
              <input type="number" className="form-control" value={settings.vatPercent || 0}
                onChange={e => setField('vatPercent', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Thank You Message</label>
              <input className="form-control" value={settings.thankYouMessage || ''}
                onChange={e => setField('thankYouMessage', e.target.value)} />
            </div>
          </div>
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Bill Number Format</label>
              <select className="form-control" value={settings.billNumberFormat || 'auto'}
                onChange={e => setField('billNumberFormat', e.target.value)}>
                <option value="auto">Auto-increment</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Bill Number Prefix</label>
              <input className="form-control" placeholder="e.g. INV-" value={settings.billNumberPrefix || ''}
                onChange={e => setField('billNumberPrefix', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Next Bill Number Counter</label>
              <input type="number" className="form-control" value={settings.billNumberCounter || 1000}
                onChange={e => setField('billNumberCounter', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Email / SMTP */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">📧 Email (SMTP) Settings</h3></div>
        <div className="card-body">
          <div className="alert alert-warning">
            Leave blank to use the default credentials configured on the server (.env). Fill these only if you want to send emails using a different Gmail/SMTP account.
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">SMTP Host</label>
              <input className="form-control" placeholder="smtp.gmail.com" value={settings.smtpHost || ''}
                onChange={e => setField('smtpHost', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">SMTP Port</label>
              <input type="number" className="form-control" placeholder="587" value={settings.smtpPort || ''}
                onChange={e => setField('smtpPort', e.target.value)} />
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">SMTP User (Email)</label>
              <input className="form-control" placeholder="your_gmail@gmail.com" value={settings.smtpUser || ''}
                onChange={e => setField('smtpUser', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">SMTP Password (App Password)</label>
              <input type="password" className="form-control" placeholder="App password" value={settings.smtpPass || ''}
                onChange={e => setField('smtpPass', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-accent btn-lg" onClick={handleSave} disabled={saving}>
          <FiSave size={15} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Employees */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">👥 Employee Accounts</h3>
          <button className="btn btn-accent btn-sm" onClick={() => setShowEmpModal(true)}>
            <FiUserPlus size={14} /> Add Employee
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 && (
                <tr><td colSpan={5}>
                  <div className="empty-state">
                    <h3>No employees yet</h3>
                    <p>Add employee accounts to let staff create customers and bills</p>
                  </div>
                </td></tr>
              )}
              {employees.map(emp => (
                <tr key={emp._id}>
                  <td style={{ fontWeight: 600 }}>{emp.name}</td>
                  <td>{emp.email}</td>
                  <td><span className="badge badge-gray">{emp.role}</span></td>
                  <td>
                    {emp.isActive
                      ? <span className="badge badge-success">Active</span>
                      : <span className="badge badge-danger">Disabled</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {emp.role !== 'admin' && (
                        <>
                          <button className="btn btn-outline btn-sm btn-icon" title="Toggle Active"
                            onClick={() => toggleEmployee(emp._id)}>
                            {emp.isActive ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                          </button>
                          <button className="btn btn-outline btn-sm btn-icon" title="Remove"
                            style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            onClick={() => deleteEmployee(emp._id, emp.name)}>
                            <FiTrash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Employee Modal */}
      {showEmpModal && (
        <div className="modal-overlay" onClick={() => setShowEmpModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">➕ Add Employee</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowEmpModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={empForm.name}
                  onChange={e => setEmpForm({ ...empForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={empForm.email}
                  onChange={e => setEmpForm({ ...empForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input type="password" className="form-control" placeholder="Min 6 characters" value={empForm.password}
                  onChange={e => setEmpForm({ ...empForm, password: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEmpModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={addEmployee} disabled={savingEmp}>
                {savingEmp ? 'Adding...' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}